import React, { ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Circle, ChevronDown } from 'lucide-react';
import { cn } from 'src/shared/helpers/utils';
import styles from './Select.module.scss';
import Tooltip, { TooltipProps } from '../tooltip/Tooltip';

export type SelectOption = {
    value: string;
    label: ReactNode;
    selectedLabel?: string;
    isDisabled?: boolean;
    labelSubText?: string;
};

type SelectProps = {
    testId?: string;
    options: SelectOption[];
    isDisabled?: boolean;
    onChange: (value: string) => void;
    selectedValue: string | undefined;
    placeholder?: string;
    label?: string;
    className?: {
        select?: string;
        container?: string;
    };
    width?: number | string;
    tooltip?: ReactElement | null;
    tooltipProps?: Partial<TooltipProps>;
    renderValueFormatter?: (value: string, options: SelectOption[]) => string;
    footerOption?: ReactNode;
};

const Select = (props: SelectProps) => {
    const {
        options,
        onChange,
        selectedValue,
        label,
        width = 150,
        isDisabled,
        tooltip,
        tooltipProps,
        placeholder,
        className,
        renderValueFormatter,
        testId = 'select',
    } = props;

    const selectClassNames = classNames(styles.select, className?.select);
    const selectContainerClassNames = classNames(styles.selectContainer, className?.container);

    const selectedOption = selectedValue ? options.find(option => option.value === selectedValue) : null;
    const displayValue = selectedValue
        ? renderValueFormatter
            ? renderValueFormatter(selectedValue, options)
            : selectedOption?.label
        : null;

    const triggerId = `${testId}-trigger`;
    const labelId = label ? `${testId}-label` : undefined;

    const selectContent = (
        <div className={selectContainerClassNames} style={{ width }} data-testid={testId}>
            {label && (
                <label htmlFor={triggerId} className='block text-sm font-medium mb-1' id={labelId}>
                    {label}
                </label>
            )}
            <SelectPrimitive.Root value={selectedValue} onValueChange={onChange} disabled={isDisabled}>
                <SelectPrimitive.Trigger
                    className={cn(
                        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
                        selectClassNames
                    )}
                    id={triggerId}
                    aria-labelledby={labelId}
                    aria-disabled={isDisabled ? true : undefined}
                    disabled={isDisabled}
                >
                    <SelectPrimitive.Value placeholder={placeholder || 'None selected'}>
                        {displayValue ? (
                            <span className='flex items-center gap-2 min-w-0'>
                                <span className='truncate'>{displayValue}</span>
                            </span>
                        ) : (
                            <span className='text-muted-foreground'>{placeholder || 'None selected'}</span>
                        )}
                    </SelectPrimitive.Value>
                    <SelectPrimitive.Icon asChild>
                        <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>

                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        className={cn(
                            'relative z-[1400] max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                            styles.list
                        )}
                        position='popper'
                    >
                        <SelectPrimitive.Viewport className='p-1 h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'>
                            {options.map((option, i) => {
                                const isSelected = option.value === selectedValue;
                                return (
                                    <SelectPrimitive.Item
                                        value={option.value}
                                        key={i}
                                        disabled={option.isDisabled}
                                        className={cn(
                                            'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                                        )}
                                    >
                                        <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
                                            {isSelected ? (
                                                <Circle className='h-3.5 w-3.5 fill-primary text-primary' />
                                            ) : (
                                                <Circle className='h-3.5 w-3.5 text-muted-foreground/50' />
                                            )}
                                        </span>
                                        <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                                    </SelectPrimitive.Item>
                                );
                            })}
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
        </div>
    );

    return tooltip ? (
        <Tooltip content={tooltip} placement='bottom' {...tooltipProps}>
            {selectContent}
        </Tooltip>
    ) : (
        selectContent
    );
};

export default Select;
